/*
PURPOSE: Validate and classify pasted music streaming URLs for the ShareMusicPanel paste flow.

USE CASES:
- Gate which pasted URLs the panel will attempt to resolve (Spotify, Bandcamp, SoundCloud,
  YouTube, and Apple Music album/song links)
- Label the detected platform for the affirmative input chip and analytics

DOMAIN BOUNDARIES:
- Pure URL classification only — no I/O, no resolution, no rendering
- Mirrors the backend's Apple paste contract (musicDiscoveryManager + appleUrlParser):
  only https album-shaped Apple URLs are accepted (songs arrive as `/album/.../{id}?i={trackId}`),
  artist URLs are not a paste target, and the storefront must be a 2-letter region code
- Does NOT decide platform support beyond URL shape (the backend remains the authority — it
  returns storefront-specific errors for any malformed storefront that slips past this gate)

ASSUMPTIONS:
- Apple Music album/song URLs follow https://music.apple.com/{storefront}/album/{slug}/{numericId}
- Apple storefronts are lowercase 2-letter region codes, optionally with a 2-letter sub-region
  (matches the backend APPLE_STOREFRONT_PATTERN); a malformed storefront fails this gate and is
  treated as an unrecognized URL
*/

/**
 * Apple Music album/song URL with a well-formed lowercase 2-letter storefront. Songs share the
 * album path and carry the track id in `?i=`; both are accepted here. Mirrors the backend
 * appleUrlParser (https, album path) + APPLE_STOREFRONT_PATTERN (storefront shape).
 */
const APPLE_MUSIC_ALBUM_URL = /^https:\/\/music\.apple\.com\/[a-z]{2}(?:-[a-z]{2})?\/album\/[^/?#]+\/\d+/;

/** URL validation patterns for the platforms the paste flow supports. */
const SUPPORTED_URL_PATTERNS = [
  APPLE_MUSIC_ALBUM_URL, // Apple Music (album + song-via-?i=)
  /^https?:\/\/open\.spotify\.com\/(track|album)\/[a-zA-Z0-9]+/, // Spotify
  /^https?:\/\/[^.]+\.bandcamp\.com\/(track|album)\//, // Bandcamp
  /^https?:\/\/(www\.)?soundcloud\.com\/[^/]+\/[^/]+/, // SoundCloud
  /^spotify:(track|album):[a-zA-Z0-9]+/, // Spotify URI
  /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)[\w-]+/, // YouTube
  /^https?:\/\/music\.youtube\.com\/watch\?v=[\w-]+/, // YouTube Music
  /^https?:\/\/(www\.)?youtube\.com\/shorts\/[\w-]+/, // YouTube Shorts
];

/**
 * Returns true when the URL is a paste target the panel will attempt to resolve.
 * Apple Music URLs only validate with a well-formed storefront; a malformed storefront fails
 * here and is treated as an unrecognized URL (the backend returns storefront-specific copy for
 * any that reach it directly).
 */
export function validateMusicUrl(url: string): boolean {
  return SUPPORTED_URL_PATTERNS.some(pattern => pattern.test(url));
}

/**
 * Human-readable platform label for the input chip and analytics.
 * Returns 'Unknown' for URLs that do not match a known platform host.
 */
export function detectPlatform(url: string): string {
  if (url.includes('music.apple.com')) return 'Apple Music';
  if (url.includes('spotify.com') || url.startsWith('spotify:')) return 'Spotify';
  if (url.includes('bandcamp.com')) return 'Bandcamp';
  if (url.includes('soundcloud.com')) return 'SoundCloud';
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'YouTube';
  return 'Unknown';
}
